/* eslint-disable react/jsx-max-depth */
import { ApolloProvider, InMemoryCache } from '@apollo/client';
import { LocationProvider } from '@gatsbyjs/reach-router';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useMediaQuery } from '@mui/system';
import { deepmerge } from '@mui/utils';
import * as Sentry from '@sentry/gatsby';
import React from 'react';
import { StickyProvider } from 'react-stickup';
import { ThemeProvider as SCThemeProvider } from 'styled-components';

import SentryErrorBoundaryFallback from '@components/ErrorBoundaryFallback/SentryErrorBoundaryFallback';

import { COOKIE_KEY_ACCESS, COOKIE_KEY_ID } from '@constants/cookie-key';
import { isProduction } from '@constants/env';

import {
  AppProvider,
  ConversionProvider,
  CurationProvider,
  DocumentViewerProvider,
  MessengerProvider,
  ModalProvider,
  SessionProvider,
  SnackbarProvider,
} from '@contexts/index';

import { useApp } from '@hooks/useApp';
import { useIsMobile } from '@hooks/useIsMobile';
import { useMessenger } from '@hooks/useMessenger';

import IntlProvider from '@intl/index';

import Ethora, { type UnreadState } from '@utils/messenger/ethora';
import { tokenStorage } from '@utils/storage';

import { createApolloClient } from '../../../apollo';
import config from '../../apollo/cache';
import { baseThemeOptions } from '../../theme';
import Palette from '../../theme/palette';

const apolloCache = new InMemoryCache(config);

function AuthProvider({ children }: React.PropsWithChildren) {
  const client = React.useMemo(() => createApolloClient(apolloCache), []);

  const handleInitialize = (idToken: string, accessToken: string) => {
    // Initialize tokens
    tokenStorage.setItem(COOKIE_KEY_ID, idToken);
    tokenStorage.setItem(COOKIE_KEY_ACCESS, accessToken);
  };

  const handleReset = () => {
    // Clear cache when session state is reset
    client.cache.reset();
    tokenStorage.removeItem(COOKIE_KEY_ID);
    tokenStorage.removeItem(COOKIE_KEY_ACCESS);
  };

  return (
    <ApolloProvider client={client}>
      <SessionProvider onInitialize={handleInitialize} onReset={handleReset}>
        <CurationProvider>
          <MessengerProvider>
            <GlobalXmppProvider>{children}</GlobalXmppProvider>
          </MessengerProvider>
        </CurationProvider>
      </SessionProvider>
    </ApolloProvider>
  );
}

const LOADING_UNREAD: UnreadState = {
  totalCount: 0,
  loading: true,
  unreadByRoom: {},
};

/** Shallow value-equality for the per-room unread counts map. */
function sameCounts(
  a: Record<string, number>,
  b: Record<string, number>
): boolean {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k])
  );
}

function GlobalXmppProvider({ children }: React.PropsWithChildren) {
  const {
    state: { settings, renderChat, token },
  } = useMessenger();

  const [unread, setUnread] = React.useState<UnreadState>(LOADING_UNREAD);

  const xmppProviderConfig = React.useMemo(() => {
    return {
      customAppToken: token,
      baseUrl: settings.baseUrl,
      xmppSettings: {
        devServer: settings.devServer,
        host: settings.host,
        conference: settings.conference,
      },
      jwtLogin: { enabled: true, token: token },
      refreshTokens: { enabled: true },
      initBeforeLoad: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // The Ethora useUnread hook can return a fresh `unreadByRoom` reference on
  // every render, so the bridge's effect may fire each render. Bail out when
  // the values are unchanged so a stream of identical updates does not cascade
  // into an infinite render loop.
  const handleUnreadUpdate = React.useCallback(
    (data: UnreadState) =>
      setUnread((prev) =>
        prev.totalCount === data.totalCount &&
        prev.loading === data.loading &&
        sameCounts(prev.unreadByRoom, data.unreadByRoom)
          ? prev
          : data
      ),
    []
  );

  // Drop cached counts whenever chat is disabled (logout, profile switch,
  // re-auth) so a later re-enable never briefly shows the previous session's
  // unread counts before the bridge reports fresh data.
  React.useEffect(() => {
    if (!renderChat || !token) {
      setUnread(LOADING_UNREAD);
    }
  }, [renderChat, token]);

  if (!renderChat || !token) {
    return (
      <Ethora.UnreadContext.Provider
        value={{ totalCount: 0, loading: false, unreadByRoom: {} }}
      >
        {children}
      </Ethora.UnreadContext.Provider>
    );
  }

  return (
    <Ethora.XmppProvider config={xmppProviderConfig}>
      <Ethora.UnreadContext.Provider value={unread}>
        <Ethora.UnreadBridge onUpdate={handleUnreadUpdate} />
        {children}
      </Ethora.UnreadContext.Provider>
    </Ethora.XmppProvider>
  );
}

function ThemedProviders({ children }: React.PropsWithChildren) {
  const {
    state: { customPalette, themeMode },
  } = useApp();
  const isMobile = useIsMobile();
  const customPaletteKey = JSON.stringify(customPalette);

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const resolvedTheme = isProduction
    ? 'light'
    : themeMode === 'system'
      ? prefersDarkMode
        ? 'dark'
        : 'light'
      : themeMode;

  const theme = React.useMemo(
    () => {
      const t = createTheme({
        ...baseThemeOptions,
        palette: deepmerge(
          resolvedTheme === 'dark' ? Palette.dark : Palette.light,
          customPalette
        ),
      });
      return t;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customPaletteKey, themeMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <SCThemeProvider theme={{ ...theme, isMobile }}>
        <StickyProvider>
          <SnackbarProvider>
            <ModalProvider>
              <DocumentViewerProvider>
                <ConversionProvider>
                  <AuthProvider>{children}</AuthProvider>
                </ConversionProvider>
              </DocumentViewerProvider>
            </ModalProvider>
          </SnackbarProvider>
        </StickyProvider>
      </SCThemeProvider>
    </ThemeProvider>
  );
}
export default function Providers({ children }: React.PropsWithChildren) {
  return (
    <Sentry.ErrorBoundary showDialog fallback={SentryErrorBoundaryFallback}>
      <IntlProvider>
        <LocationProvider>
          <AppProvider>
            <ThemedProviders>{children}</ThemedProviders>
          </AppProvider>
        </LocationProvider>
      </IntlProvider>
    </Sentry.ErrorBoundary>
  );
}
